import { useState } from "react"
import { DateTime } from "luxon"

//Helpers
import { LogDebug, LogError } from "../../../helpers/logger"
import { displayLocalDateTimeFromIso } from "../../../helpers/displayDates"

//Notifications resources:
import markAsReadIcon from "../resources/mark_as_read_icon.svg"
import markAsUnreadIcon from "../resources/mark_as_unread_icon.svg"

//prime react
import { AccordionTab } from "primereact/accordion"
import { Button } from "primereact/button"
import { Tag } from "primereact/tag"
import { Tooltip } from "primereact/tooltip"
import { Badge } from "primereact/badge"
import { classNames } from "primereact/utils"

//styles
import noteStyle from "../../../styles/notifications/NotificationsForm.module.css"

export default function NotificationCard({
  notification,
  getOrganizationName,
  onMarkAsRead,
  onMarkAsUnread,
  onActionButtonClick,
  onExpandDescription,
  onCollapseDescription,
}) {
  const descriptionStripped = (description) => {
    return description.replace(/<[^>]+>/g, "")
  }
  const isSystemNotification = notification.systemNotification

  const itemHeader = (data) => {
    const strippedDescription = descriptionStripped(data.description)

    const doTruncate = strippedDescription.length > 160

    return (
      <div className={noteStyle.noteHeaderGridContainer}>
        <Tooltip target=".header-disabled-tt" />
        <div
          className={noteStyle.unread}
          style={data.read ? { visibility: "hidden" } : {}}
        />
        <div className={noteStyle.noteHeaderTitleDesc}>
          <div className={noteStyle.noteHeaderGridItemTitle}>
            {data.title}
            {data.highPriority && (
              <Badge
                value="HIGH PRIORITY"
                className={noteStyle.noteHighImportant}
                severity="danger"
              />
            )}
          </div>
          <div className={noteStyle.noteHeaderGridItemDescr}>
            {!data.descrExpanded ? (
              <>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {doTruncate
                    ? strippedDescription.substring(0, 157) + "..."
                    : strippedDescription}
                </div>
                {doTruncate && (
                  <Button
                    className={classNames(
                      noteStyle.noteMarkAsReadBtn,
                      noteStyle.toggleDescriptionButton,
                    )}
                    icon="pi pi-angle-down"
                    iconPos="right"
                    severity="secondary"
                    onClick={() => {
                      onExpandDescription()
                    }}
                  />
                )}
              </>
            ) : (
              <div style={{ whiteSpace: "pre-wrap" }}>
                {data.description}
                <Button
                  className={classNames(
                    noteStyle.noteMarkAsReadBtn,
                    noteStyle.toggleDescriptionButton,
                  )}
                  icon="pi pi-angle-up"
                  iconPos="right"
                  severity="secondary"
                  onClick={() => {
                    onCollapseDescription()
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {isSystemNotification && (
          <Tag
            value="system notification"
            className={noteStyle.systemNot}
            severity="info"
          />
        )}
        <div className={noteStyle.noteHeaderOther}>
          {!data.read ? (
            <Button
              className={noteStyle.noteMarkAsReadBtn}
              onClick={(e) => {
                if (!isSystemNotification) e.stopPropagation()
                onMarkAsRead(data.id, isSystemNotification)
              }}
            >
              <img src={markAsReadIcon} alt="mark as read" /> Mark read
            </Button>
          ) : (
            <Button
              className={noteStyle.noteMarkAsUnreadReadBtn}
              onClick={(e) => {
                if (!isSystemNotification) e.stopPropagation()
                onMarkAsUnread(data.id, isSystemNotification)
              }}
            >
              <img src={markAsUnreadIcon} alt="mark unread" /> Mark unread
            </Button>
          )}
          <div>
            <div className={noteStyle.noteHeaderGridItemCreation}>
              <i
                className="pi pi-clock"
                style={{
                  fontSize: "0.75rem",
                  lineHeight: "0.75rem",
                  marginRight: "5px",
                }}
              />
              {DateTime.fromISO(data.createdAt, { zone: "utc" })
                .setZone("local")
                .toLocaleString(DateTime.DATETIME_SHORT)}{" "}
              <br />
            </div>
            <div className={noteStyle.noteHeaderGridItemOrg}>
              {getOrganizationName(data.groupId)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const actionButton = (label, url, id, notification) => {
    return (
      <>
        {url ? (
          <Button
            className={`p-button-rounded p-button-secondary ${noteStyle.noteBodyActionButton}`}
            onClick={() => {
              onActionButtonClick(url, notification, id)
            }}
            label={label}
          />
        ) : null}
      </>
    )
  }

  return (
    <AccordionTab
      headerTemplate={itemHeader(notification)}
      headerStyle={{ width: "100%" }}
      key={notification.id}
      className={
        notification.systemNotification ? noteStyle.systemNotification : ""
      }
    >
      <div className={noteStyle.noteBodyContainer}>
        <div className={noteStyle.noteBodyInfo}>
          <div>
            <span className={classNames(noteStyle.label, "mr-1")}>
              Last modified:
            </span>
            {DateTime.fromISO(notification.modifiedAt, { zone: "utc" })
              .setZone("local")
              .toLocaleString(DateTime.DATETIME_SHORT)}
          </div>
          <div>
            <label className={classNames(noteStyle.label, "mr-1")}>
              Read on:
            </label>
            {notification.read
              ? DateTime.fromISO(notification.readAt, { zone: "utc" })
                  .setZone("local")
                  .toLocaleString(DateTime.DATETIME_SHORT)
              : "Unread"}
          </div>
        </div>
        <Tooltip target=".action-disabled-tt" />
        <div className={noteStyle.noteBodyActionButtons}>
          {actionButton(
            notification.primaryActionLabel,
            notification.primaryActionUrl,
            notification.id,
            notification,
          )}
          {actionButton(
            notification.secondaryActionLabel,
            notification.secondaryActionUrl,
            notification.id,
            notification,
          )}
        </div>
      </div>
    </AccordionTab>
  )
}
